import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  pushSupported, pushPermissionState, currentSubscription,
  enablePush, disablePush, sendTestPush,
} from "@/lib/pushClient";
import { Bell, BellOff, BellRing, ShieldAlert, Smartphone } from "lucide-react";
import { toast } from "sonner";

/**
 * PushSettingsCard — installable web-push toggle for admins, vendors, and
 * residents. The heavy lifting is in `lib/pushClient.js`; this component
 * just orchestrates permission → subscription → test call. Also serves as
 * the entry point for the /app/mobile Expo React Native wrapper (which
 * shares the same push_subscriptions collection on the backend).
 */
export default function PushSettingsCard() {
  const [supported, setSupported] = useState(false);
  const [perm, setPerm] = useState("unsupported");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const s = pushSupported();
    setSupported(s);
    if (!s) { setPerm("unsupported"); setSubscribed(false); return; }
    setPerm(await pushPermissionState());
    const sub = await currentSubscription();
    setSubscribed(!!sub);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const onEnable = async () => {
    setBusy(true);
    try {
      await enablePush();
      toast.success("Push notifications enabled");
      await refresh();
    } catch (e) { toast.error(e?.message || "Couldn't enable notifications"); }
    setBusy(false);
  };
  const onDisable = async () => {
    setBusy(true);
    try {
      await disablePush();
      toast.success("Push notifications turned off");
      await refresh();
    } catch (e) { toast.error(e?.message || "Couldn't disable"); }
    setBusy(false);
  };
  const onTest = async () => {
    setBusy(true);
    try {
      const { data } = await sendTestPush();
      if (data?.ok) toast.success(`Test sent to ${data.sent} device(s)`);
      else toast.error(data?.reason === "no_subscriptions"
                        ? "No devices subscribed yet — enable push first."
                        : "Test failed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    setBusy(false);
  };

  return (
    <Card className="border-white/10 bg-card p-5" data-testid="push-settings-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <BellRing className="h-4 w-4 text-orange-300" />
            <div className="font-semibold text-sm">Push notifications</div>
            {subscribed ? (
              <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 text-[10px]" data-testid="push-status-on">On</Badge>
            ) : supported ? (
              <Badge variant="outline" className="border-white/15 text-zinc-400 text-[10px]" data-testid="push-status-off">Off</Badge>
            ) : (
              <Badge variant="outline" className="border-white/15 text-zinc-500 text-[10px]" data-testid="push-status-unsupported">Not available in this browser</Badge>
            )}
          </div>
          <div className="text-[11px] text-zinc-500 mt-1">
            Get instant desktop + mobile alerts for delinquent accounts, board digests,
            new work orders, RFP awards, and payment confirmations. Works on Chrome,
            Edge, Firefox, and Safari 16.4+ (iOS home-screen install).
          </div>
        </div>
        <Smartphone className="h-5 w-5 text-zinc-500 shrink-0" />
      </div>

      <div className="flex flex-wrap gap-2">
        {!subscribed && supported && (
          <Button onClick={onEnable} disabled={busy}
                  className="bg-orange-500 hover:bg-orange-600 gap-2"
                  data-testid="push-enable-btn">
            <Bell className="h-4 w-4" />
            {perm === "denied" ? "Notification permission blocked — reset in browser" : "Enable notifications"}
          </Button>
        )}
        {subscribed && (
          <>
            <Button variant="outline" onClick={onTest} disabled={busy}
                    className="border-white/10 gap-2"
                    data-testid="push-test-btn">
              <BellRing className="h-4 w-4" /> Send test
            </Button>
            <Button variant="ghost" onClick={onDisable} disabled={busy}
                    className="text-rose-400 hover:text-rose-300 gap-2"
                    data-testid="push-disable-btn">
              <BellOff className="h-4 w-4" /> Turn off
            </Button>
          </>
        )}
      </div>

      {perm === "denied" && (
        <div className="mt-3 text-[11px] text-amber-300 flex items-start gap-1.5" data-testid="push-blocked-hint">
          <ShieldAlert className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
          Your browser has blocked notification permission. Reset it via the address bar
          padlock icon → Site settings → Notifications → Allow, then reload.
        </div>
      )}

      <div className="mt-3 text-[10px] text-zinc-600">
        Mobile app coming? See <span className="font-mono">/app/mobile/README.md</span> — same
        push endpoint powers both web + React Native builds.
      </div>
    </Card>
  );
}
